import React, { useEffect, useLayoutEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Icon } from '@iq/react-components';

import { setActiveComponentId } from '../../../../../bundles/application';
import { useComponentPanel } from '../../ComponentPanelState';
import ComponentSelect from '../../../../ComponentSelect';
import TreeItem from './TreeItem';

const ComponentTree = () => {
  const dispatch = useDispatch();

  const {
    tree: componentTree,
    endingDepth,
    expanded: displayExpanded,
    flatList: flat,
    modelsHashmap,
    pageIndex,
    activeComponentId,
    pageComponents: shown,
    startingDepth,
    withToolbar: toolbar,
    createdComponent,
    showDeleteTreeButton,
    canDelete,
    rootComponent,
    setShowDeleteDialog,
    scrollToActive,
    enableAutoScroll,
  } = useComponentPanel();

  const expandedDepth = displayExpanded ? startingDepth + 1 : startingDepth;
  const findExpandedIds = useCallback(
    (node, depth = 1) => {
      if (!node) return shown?.map((c) => c.id) || [];
      const expandedIds = [];
      const xDepth = !expandedDepth ? Infinity : expandedDepth;
      if (depth <= xDepth && node.children?.length) {
        expandedIds.push(node.id);
        node.children.forEach((child) => expandedIds.push(...findExpandedIds(child, depth + 1)));
      }
      return expandedIds;
    },
    [expandedDepth, shown]
  );

  const shownIds = useMemo(() => shown?.map((c) => c.id) || [], [shown]);

  const [showFilters, setShowFilters] = useState(false);
  const [newComponent, setNewComponent] = useState({});
  const [expandedIdsByPage, setExpandedIdsByPage] = useState(
    flat ? {} : { [pageIndex]: findExpandedIds(componentTree) }
  );

  const filterPanel = useRef(null);
  const componentsRef = useRef({});
  const compsList = useRef(null);

  const onSearchSelect = (selection) => {
    dispatch(setActiveComponentId(selection));
  };

  const reset = useCallback(() => {
    dispatch(setActiveComponentId(null));
    setExpandedIdsByPage((prev) => ({ ...prev, [pageIndex]: findExpandedIds(componentTree) }));
  }, [pageIndex, findExpandedIds, componentTree]);

  useEffect(() => {
    setExpandedIdsByPage((prev) => {
      if (!expandedIdsByPage[pageIndex]) {
        return { ...prev, [pageIndex]: findExpandedIds(componentTree) };
      }
      return prev;
    });
  }, [expandedIdsByPage, pageIndex, findExpandedIds, componentTree]);

  // Consolidated effect to handle both activeComponent and activeComponentId expansion
  useEffect(() => {
    if (activeComponentId) {
      const selected = shown?.find((comp) => comp.id === activeComponentId);
      const selectedIds = selected ? selected.ancestorIds : [];

      setExpandedIdsByPage((prev) => {
        const currentExpanded = prev[pageIndex] || [];
        const newIds = [activeComponentId, ...selectedIds].filter(
          (id) => !currentExpanded.includes(id)
        );

        if (newIds.length > 0) {
          return {
            ...prev,
            [pageIndex]: [...currentExpanded, ...newIds],
          };
        }
        return prev;
      });
    }
  }, [shown, activeComponentId, pageIndex]);

  useEffect(() => {
    setNewComponent(createdComponent);
  }, [createdComponent]);

  useLayoutEffect(() => {
    // Use a small timeout to ensure DOM is updated after expansion changes
    const scrollTimeout = setTimeout(() => {
      const item = componentsRef.current?.[activeComponentId];
      const list = compsList.current;
      if (item && list && expandedIdsByPage[pageIndex]?.includes(activeComponentId)) {
        const { top: iT, bottom: iB } = item.getBoundingClientRect();
        const { top: lT, bottom: lB } = list.getBoundingClientRect();
        const shouldScroll =
          (list.scrollHeight > list.clientHeight && (iT < lT || iB > lB + 30)) || false;
        if (shouldScroll && scrollToActive && activeComponentId) {
          item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          // Reset scroll state after scrolling happens
          setTimeout(() => enableAutoScroll(), 100);
        }
      }
    }, 10); // Small delay to allow DOM updates

    if (newComponent) {
      const newComponentRef = componentsRef.current[newComponent.id];
      if (newComponentRef) {
        newComponentRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      setNewComponent();
    }

    return () => clearTimeout(scrollTimeout);
  }, [activeComponentId, newComponent, pageIndex, expandedIdsByPage]);

  useLayoutEffect(() => {
    if (newComponent) {
      setExpandedIdsByPage((prev) => ({
        ...prev,
        [pageIndex]: [...(prev[pageIndex] || []), newComponent.parent],
      }));
    }
  }, [newComponent]);

  const handleClick = (e) => {
    if (!filterPanel.current?.contains(e.target)) {
      e.stopPropagation();
      setShowFilters(false);
    }
  };

  useEffect(() => {
    if (showFilters) {
      document.addEventListener('click', handleClick, true);
    } else {
      document.removeEventListener('click', handleClick, true);
    }
    return () => document.removeEventListener('click', handleClick, true);
  }, [showFilters]);

  const toggleExpansion = (id) => {
    if (expandedIdsByPage[pageIndex]?.includes(id)) {
      setExpandedIdsByPage((prev) => ({
        ...prev,
        [pageIndex]: prev[pageIndex].filter((i) => i !== id),
      }));
    } else {
      setExpandedIdsByPage((prev) => ({
        ...prev,
        [pageIndex]: [...(prev[pageIndex] || []), id],
      }));
    }
  };

  const mergeNodeWithItem = (node, shown) => {
    const item = shown?.find((s) => s.id === node.id);
    if (!item) return node;
    const merged = { ...item, ...node };
    if (Array.isArray(node.children)) {
      merged.children = node.children.map((child) => mergeNodeWithItem(child, shown));
    }
    return merged;
  };

  const renderTree = (node, depth = 1, parentDesignation = '') => {
    const { id, children = [] } = node;

    const end = endingDepth === 0 ? Infinity : endingDepth;
    let nodes = null;
    let hasVisibleChildren = false;

    if (!id) return null;
    if (children.length) {
      const itemDesignationRef = depth >= startingDepth ? node.itemDesignation : '';
      nodes = children.map((child) => renderTree(child, depth + 1, itemDesignationRef));
      children.forEach((child) => {
        if (!hasVisibleChildren && shownIds.includes(child.id) && depth < end) {
          hasVisibleChildren = true;
        }
      });
    }

    const item = mergeNodeWithItem(node, shown);

    if (!flat) {
      if (parentDesignation && node.itemDesignation.indexOf(parentDesignation) === 0) {
        item.simpleDesignation = item.itemDesignation.replace(parentDesignation, '');
      }
      item.models = (item.models || []).map((m) => ({ ...m, ...modelsHashmap[m.modelId] }));
    }

    if (shownIds.includes(id) && depth >= startingDepth && depth <= end) {
      return (
        <TreeItem
          key={id}
          ref={(el, compId) => {
            componentsRef.current[compId] = el;
          }}
          depth={depth - startingDepth + 1}
          expanded={!!expandedIdsByPage[pageIndex]?.includes(id)}
          hasChildren={hasVisibleChildren}
          item={item}
          isSelected={id === activeComponentId}
          toggleExpansion={toggleExpansion}
          treeRoot={compsList.current}
        >
          {nodes}
        </TreeItem>
      );
    }
    return nodes;
  };

  return (
    <div
      className="components-panel--tree"
      role="tree"
    >
      {toolbar ? (
        <div className="tree-toolbar">
          <ComponentSelect
            isMulti={false}
            value={activeComponentId}
            onChange={onSearchSelect}
            closeMenuOnSelect
            placeholder="Search components..."
            availableComponents={shown || []}
            isClearable
            rootComponentId={rootComponent?.id}
            styles={{
              control: {
                minHeight: 'unset',
                lineHeight: 'unset',
              },
              clearIndicator: {
                padding: '6px',
              },
              dropdownIndicator: {
                padding: '6px',
              },
            }}
          />
          <Button
            onClick={reset}
            activity="secondary"
            icon={<Icon icon="he-load" />}
            tooltip="Reset component tree"
          />
          {canDelete && showDeleteTreeButton ? (
            <Button
              onClick={() => setShowDeleteDialog(true)}
              activity="danger"
            >
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <div
        className={`tree-root ${!flat && !toolbar ? 'full' : ''} custom-thin-scrollbar`}
        ref={compsList}
        onClick={() => dispatch(setActiveComponentId(activeComponentId))}
      >
        {renderTree(componentTree)}
      </div>
    </div>
  );
};

export default ComponentTree;
